import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// Types
import UserRoles from 'types/UserRoles';
// Models
import { IUser } from 'models/User';
import IAccount from 'models/Account';
// Async
import { getUsers, deleteUser, inviteStaff, inviteOwner, inviteAdministrator, changePassword, recoverPassword } from './userAsync';
import AccountsAsync from 'store/accounts/accountsAsync';

interface IState {
  users: IUser[] | null;
  filter: {
    search: string;
    group: UserRoles | string;
  }
  loading: boolean;
};

const initialState:IState = {
  users: null,
  filter: {
    search: '',
    group: ''
  },
  loading: false
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setFilter: (state, action:PayloadAction<{ field:keyof IState['filter'], value:string }>) => {
      state.filter[action.payload.field] = action.payload.value;
    }
  },
  extraReducers: (builder) => {
    // Get users
    builder.addCase(getUsers.pending, (state) => {
      state.users = null
    })
    builder.addCase(getUsers.fulfilled, (state, action:PayloadAction<any[]>) => {
      state.users = action.payload.map((user:IUser) => {
        return {
          ...user,
          licenseExpirationDate: user.account?.licenseExpirationDate || null,
        }
      });
    })
    // Create password
    builder.addCase(changePassword.pending, (state) => {
      state.loading = true
    })
    // Recover password
    builder.addCase(recoverPassword.pending, (state) => {
      state.loading = true
    })
    // Delete user
    builder.addCase(deleteUser.pending, (state) => {
      state.loading = true
    })
    builder.addCase(deleteUser.fulfilled, (state, action:PayloadAction<any>) => {
      if ( state.users ){
        state.users = state.users.filter((user:IUser) => user._id !== action.payload)
      }
    })
    // Invite staff
    builder.addCase(inviteStaff.pending, (state) => {
      state.loading = true
    })
    builder.addCase(inviteStaff.fulfilled, (state, action:PayloadAction<IUser>) => {
      if ( state.users ){
        state.users.push(action.payload);
      }
    })
    // Invite owner
    builder.addCase(inviteOwner.pending, (state) => {
      state.loading = true
    })
    builder.addCase(inviteOwner.fulfilled, (state, action:PayloadAction<IUser>) => {
      if ( state.users ){
        state.users.push(action.payload);
      }
    })
    // Invite administrator
    builder.addCase(inviteAdministrator.pending, (state) => {
      state.loading = true
    })
    builder.addCase(inviteAdministrator.fulfilled, (state, action:PayloadAction<IUser>) => {
      if ( state.users ){
        state.users.push(action.payload);
      }
    })

    // Update account
    builder.addCase(AccountsAsync.updateAccount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(AccountsAsync.updateAccount.fulfilled, (state, action:PayloadAction<IAccount>) => {
      if (state.users) {
        state.users = state.users.map((user:IUser) => {
          if (user.account && user.account._id === action.payload._id) {
            return { ...user, account: action.payload };
          }
          return user;
        }
        );
      }
    });
    // Disable accounts
    builder.addCase(AccountsAsync.disableAccount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(AccountsAsync.disableAccount.fulfilled, (state, action:PayloadAction<IAccount>) => {
      if (state.users) {
        state.users = state.users.map((user:IUser) => {
          if (user.account && user.account._id === action.payload._id) {
            return { ...user, account: action.payload };
          }
          return user;
        }
        );
      }
    });
    // Enable accounts
    builder.addCase(AccountsAsync.enableAccount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(AccountsAsync.enableAccount.fulfilled, (state, action:PayloadAction<IAccount>) => {
      if (state.users) {
        state.users = state.users.map((user:IUser) => {
          if (user.account && user.account._id === action.payload._id) {
            return { ...user, account: action.payload };
          }
          return user;
        }
        );
      }
    });

    builder.addMatcher(
      (action) => action.type.includes('/fulfilled') || action.type.includes('/rejected'),
      (state) => {
        state.loading = false;
      }
    )
  }
});

export const { setFilter } = slice.actions;

export default slice.reducer;
